import { useState } from 'react'

import { useToasts } from '@/hooks/useToasts'
import { SubmitHandler } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

import {
  CaseDocument,
  useFacilityNamesQuery,
  useUpdateCaseDetailsMutation,
  useUsersQuery,
} from '@/graphql/generated/hooks'
import {
  getFacilityOptions,
  getUserOptions,
} from '@/utils/forms/optionBuilders'
import { MIN_CHAR_COUNT, SEARCH_DEBOUNCE_TIME } from '@/utils/forms/validation'

import { UpdateCaseFormInputsIProps } from '../types/types'

interface UseUpdateCaseIProps {
  onClose: () => void
  caseId: string
}

export const useUpdateCase = ({ onClose, caseId }: UseUpdateCaseIProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const { showSuccess, showError } = useToasts()
  const [facilityName, setFacilityName] = useState('')
  const [debouncedfacilityName] = useDebounce(
    facilityName,
    SEARCH_DEBOUNCE_TIME
  )

  const { data: optionsData, loading: isUsersLoading } = useUsersQuery({
    fetchPolicy: 'network-only',
    variables: {
      filter: { roleNameIn: ['OPERATOR', 'PROGRAM_MANAGER'] },
    },
  })
  const { data: facilitiesData, loading: isFacilitiesLoading } =
    useFacilityNamesQuery({
      fetchPolicy: 'network-only',
      variables: {
        filter:
          debouncedfacilityName?.length >= MIN_CHAR_COUNT
            ? { nameLike: debouncedfacilityName }
            : undefined,
      },
    })

  const [updateCase] = useUpdateCaseDetailsMutation()

  const facilityOptions = getFacilityOptions(facilitiesData)
  const userOptions = getUserOptions(optionsData)

  const onUpdateCase: SubmitHandler<UpdateCaseFormInputsIProps> = async (
    values
  ) => {
    setIsLoading(true)
    try {
      await updateCase({
        variables: {
          caseId: caseId,
          caseName: values?.name,
          caseFacilityIds: values?.facilities?.map((f) => f.value) || [],
          caseOwnerId: values?.owner?.value,
          status: values?.status?.value,
        },
        refetchQueries: [
          {
            query: CaseDocument,
            variables: { id: caseId },
          },
        ],
      })
      showSuccess(`Successfully updated ${values?.name} Case`)
      setIsLoading(false)
      onClose()
    } catch (error) {
      showError(error.message)
      setIsLoading(false)
    }
  }

  return {
    facilityOptions,
    userOptions,
    isLoading,
    isFacilitiesLoading,
    isUsersLoading,
    onUpdateCase,
    setFacilityName,
  }
}
